// api/providers/moviesContinueWatching.js
import { playListPassword, playListUsername, playListUrl } from "../../../state";
import { convertItemsToTilesMovies, chunkArraySeries } from "../formatters/ItemFormatter";
import { fetchData } from "../workers/fetchWorkerManager";
import { dnsResolver } from "./dnsResolver";

// Cache to prevent refetching
const continueWatchingCache = new Map();
const CACHE_DURATION = 5 * 60 * 1000; // 5 minutes

// ✅ ADDED: Helper to get current playlist identifier
const getCurrentPlaylistId = () => {
  return `${playListUrl()}_${playListUsername()}`;
};

// ✅ MODIFIED: Filter by current playlist
const getContinueWatchingMovies = () => {
  try {
    const continueWatchingData = localStorage.getItem("vod_continue_watching");
    if (!continueWatchingData) return [];

    const allWatching = JSON.parse(continueWatchingData);
    const currentPlaylist = getCurrentPlaylistId();

    // Filter only continue watching from current playlist
    const playlistWatching = allWatching.filter(item => item.playlistId === currentPlaylist);

    // Sort by most recently watched (lastWatchedAt timestamp)
    return playlistWatching.sort((a, b) => b.lastWatchedAt - a.lastWatchedAt);
  } catch (error) {
    console.error("Error reading continue watching data:", error);
    return [];
  }
};

// Build API URL with DNS support
const buildApiUrl = async (baseUrl, username, password, action, additionalParams = "") => {
  let apiUrl = `${baseUrl}/player_api.php?username=${username}&password=${password}&action=${action}`;
  if (additionalParams) {
    apiUrl += `&${additionalParams}`;
  }

  let finalApiUrl = apiUrl;
  try {
    const dnsResult = await dnsResolver.transformUrl(apiUrl);
    if (typeof dnsResult === "object" && dnsResult.url) {
      finalApiUrl = dnsResult.url;
    } else if (typeof dnsResult === "string" && dnsResult !== apiUrl) {
      finalApiUrl = dnsResult;
    }
  } catch (dnsError) {
    console.warn("DNS transformation failed:", dnsError);
  }

  return finalApiUrl;
};

export default function (filter) {
  return async pageIndex => {
    const username = playListUsername();
    const password = playListPassword();
    const baseUrl = playListUrl();

    if (!username || !password || !baseUrl) {
      console.error("Missing required parameters for Continue Watching");
      return [];
    }

    const cacheKey = `${baseUrl}_${username}_continue_watching`;

    // Check cache
    const cached = continueWatchingCache.get(cacheKey);
    if (cached && Date.now() - cached.timestamp < CACHE_DURATION) {
      console.log("Using cached continue watching data");
      return cached.data;
    }

    console.log("Loading Continue Watching movies...");

    try {
      // Get continue watching data from localStorage
      const continueWatchingList = getContinueWatchingMovies();

      if (continueWatchingList.length === 0) {
        console.log("No continue watching movies found");
        return [];
      }

      console.log(`Found ${continueWatchingList.length} continue watching entries`);

      // Extract unique stream IDs and category IDs
      const movieIds = new Set();
      const categoryIds = new Set();

      continueWatchingList.forEach(item => {
        try {
          const urlParts = item.videoUrl.split("/");
          const filenamePart = urlParts[urlParts.length - 1];
          const streamId = filenamePart.split(".")[0];
          movieIds.add(streamId);

          if (item.category_id) {
            categoryIds.add(item.category_id);
          }
        } catch (err) {
          console.warn("Error parsing continue watching URL:", err);
        }
      });

      console.log(`Fetching data for ${movieIds.size} movies from ${categoryIds.size} categories`);

      // Fetch actual movie data from categories
      const allMovies = [];

      for (const categoryId of categoryIds) {
        try {
          const categoryUrl = await buildApiUrl(
            baseUrl,
            username,
            password,
            "get_vod_streams",
            `category_id=${categoryId}`,
          );

          const movies = await fetchData(categoryUrl);

          if (Array.isArray(movies)) {
            allMovies.push(...movies);
          }
        } catch (error) {
          console.warn(`Failed to fetch category ${categoryId}:`, error);
        }
      }

      // Match fetched movies with continue watching list
      const matchedMovies = [];

      for (const cwItem of continueWatchingList) {
        try {
          const urlParts = cwItem.videoUrl.split("/");
          const filenamePart = urlParts[urlParts.length - 1];
          const streamId = filenamePart.split(".")[0];

          // Find matching movie from fetched data
          const movie = allMovies.find(m => String(m.stream_id) === String(streamId));

          if (movie) {
            // Add continue watching metadata including duration for progress bar
            matchedMovies.push({
              ...movie,
              continue_watching_time: cwItem.currentTime,
              continue_watching_duration: cwItem.duration || 0,
              continue_watching_timestamp: cwItem.lastWatchedAt,
            });
          } else {
            // Fallback: create basic movie object if not found in API
            matchedMovies.push({
              stream_id: parseInt(streamId),
              name: cwItem.name || `Movie ${streamId}`,
              stream_icon: cwItem.poster || "",
              category_id: cwItem.category_id || "unknown",
              container_extension: filenamePart.split(".")[1] || "mkv",
              rating_5based: cwItem.rating ? cwItem.rating / 2 : 0, // Convert 10-based to 5-based
              continue_watching_time: cwItem.currentTime,
              continue_watching_duration: cwItem.duration || 0,
              continue_watching_timestamp: cwItem.lastWatchedAt,
              added: cwItem.lastWatchedAt.toString(),
              plot: "Continue watching",
            });
          }
        } catch (err) {
          console.warn("Error processing continue watching item:", err);
        }
      }

      console.log(`Matched ${matchedMovies.length} movies with full data`);

      // Convert to tiles (limit to 20 most recent)
      const limitedMovies = matchedMovies.slice(0, 20);
      const processedTiles = convertItemsToTilesMovies(limitedMovies);

      // Cache the result
      continueWatchingCache.set(cacheKey, {
        data: processedTiles,
        timestamp: Date.now(),
      });

      console.log(`Continue watching processed: ${processedTiles.length} tiles`);
      return processedTiles;
    } catch (error) {
      console.error("Error getting Continue Watching movies:", error);
      return [];
    }
  };
}

// ✅ MODIFIED: Add playlistId when saving
export const addToContinueWatching = (videoUrl, currentTime, movieData = {}) => {
  try {
    const continueWatchingData = localStorage.getItem("vod_continue_watching");
    let watchingList = continueWatchingData ? JSON.parse(continueWatchingData) : [];

    const currentPlaylist = getCurrentPlaylistId();

    const existingIndex = watchingList.findIndex(
      item => item.videoUrl === videoUrl && item.playlistId === currentPlaylist,
    );

    const movieEntry = {
      videoUrl,
      currentTime,
      duration: movieData.duration || 0, // Store duration for progress calculation
      lastWatchedAt: Date.now(),
      name: movieData.name || "",
      poster: movieData.poster || "",
      category_id: movieData.category_id || "",
      rating: movieData.rating || 0,
      playlistId: currentPlaylist, // ✅ ADDED THIS LINE
    };

    if (existingIndex !== -1) {
      watchingList[existingIndex] = movieEntry;
    } else {
      watchingList.push(movieEntry);
    }

    if (watchingList.length > 50) {
      watchingList = watchingList.slice(-50);
    }

    localStorage.setItem("vod_continue_watching", JSON.stringify(watchingList));

    // Clear cache to force refresh
    continueWatchingCache.clear();

    console.log("Added to continue watching:", movieData.name || videoUrl);
  } catch (error) {
    console.error("Error adding to continue watching:", error);
  }
};

// ✅ MODIFIED: Check only in current playlist
export const getContinueWatchingTime = videoUrl => {
  try {
    const continueWatchingData = localStorage.getItem("vod_continue_watching");
    if (!continueWatchingData) return null;

    const watchingList = JSON.parse(continueWatchingData);
    const currentPlaylist = getCurrentPlaylistId();

    const entry = watchingList.find(
      item => item.videoUrl === videoUrl && item.playlistId === currentPlaylist,
    );

    return entry ? entry.currentTime : null;
  } catch (error) {
    console.error("Error getting continue watching time:", error);
    return null;
  }
};

// ✅ MODIFIED: Remove only from current playlist
export const removeFromContinueWatching = videoUrl => {
  try {
    const continueWatchingData = localStorage.getItem("vod_continue_watching");
    if (!continueWatchingData) return;

    let watchingList = JSON.parse(continueWatchingData);
    const currentPlaylist = getCurrentPlaylistId();

    watchingList = watchingList.filter(
      item => !(item.videoUrl === videoUrl && item.playlistId === currentPlaylist),
    );

    localStorage.setItem("vod_continue_watching", JSON.stringify(watchingList));
    continueWatchingCache.clear();

    console.log("Removed from continue watching:", videoUrl);
  } catch (error) {
    console.error("Error removing from continue watching:", error);
  }
};

export const clearContinueWatching = () => {
  localStorage.removeItem("vod_continue_watching");
  continueWatchingCache.clear();
  console.log("Cleared all continue watching data");
};
